<template>
  <CCollapsibleCard theme="shadow-low" class="c-time-registration-card">
    <template #toggle>
      <CShelf>
        <CAvatar :userId="registration.startedBy.id" class="u-mr-4"/>
        <CIcon :name="icon"/>
        <h3>{{ $t('timeRegistrationModal.block', { number: registration.sequenceNumber }) }}</h3>
        {{ formatRelativeDateTime(registration.startedOn) }}
        <span v-if="registration.stoppedBy">- {{ formatRelativeDateTime(registration.stoppedOn) }}</span>
        <span v-if="timeUnitQuantity !== undefined">
          {{ $t('timeRegistrationModal.timeUnit', { timeUnit: timeUnitQuantity }) }}
        </span>
      </CShelf>
    </template>

    <template #content>
      <div class="u-p-4">
        <CSectionCard v-if="registration.stoppedBy">
          <CSectionItem v-for="job in registration.jobs" :key="job.id">
            <CJob :job display="time-registration">
              <template v-if="job.allowedMinutes" #right>
                {{ durationFromMinutes(job.allowedMinutes) }}
              </template>
            </CJob>
          </CSectionItem>
        </CSectionCard>

        <CForm v-else>
          <CShelf class="u-mb-4">
            <CButton theme="main-primary" @click="finish">
              {{ $t(hasAllSelected ? 'timeRegistrationModal.stopAll' : 'timeRegistrationModal.stopSelected', selectedJobs.length) }}
            </CButton>
            <CButton theme="normal-primary" @click="pause">
              {{ $t(hasAllSelected ? 'timeRegistrationModal.pauseAll' : 'timeRegistrationModal.pauseSelected', selectedJobs.length) }}
            </CButton>
          </CShelf>
          <CField v-model="selectedJobs" type="checklist" :options="registration.jobs" :disabledOptions="disabledJobs">
            <template #option="{option}">
              <CJob :key="option.id" :job="option">
                <template v-if="option.allowedMinutes" #right>
                  {{ durationFromMinutes(option.allowedMinutes) }}
                </template>
              </CJob>
            </template>
          </CField>
        </CForm>
      </div>
    </template>
  </CCollapsibleCard>
</template>

<script setup lang="ts">
  import moment from 'moment';
  import { computed, reactive, Ref, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import WorkStatus from '@/dossier/constants/WorkStatus';
  import Modals from '@/general/services/Modals';
  import Modal from '@/general/utils/Modal';
  import CAvatar from '@/shared/components/CAvatar.vue';
  import CCollapsibleCard from '@/shared/components/CCollapsibleCard.vue';
  import CIcon from '@/shared/components/CIcon.vue';
  import CShelf from '@/shared/components/CShelf.vue';
  import CButton from '@/shared/components/buttons/CButton.vue';
  import CField from '@/shared/components/form/CField.vue';
  import CForm from '@/shared/components/form/CForm.vue';
  import CSectionCard from '@/shared/components/section/CSectionCard.vue';
  import CSectionItem from '@/shared/components/section/CSectionItem.vue';
  import { durationFromMinutes, formatRelativeDateTime } from '@/shared/utils/dateTime';
  import { isPresent } from '@/shared/utils/typescript';
  import Job from '../../models/Job';
  import JobContext from '../../models/JobContext';
  import TimeRegistration from '../../models/TimeRegistration';
  import JobService from '../../services/JobService';
  import TimeRegistrationService from '../../services/TimeRegistrationService';
  import CJob from '../CJob.vue';

  const PauseTimeRegistrationModal = () => import('../../modals/PauseTimeRegistrationModal.vue');

  const props = defineProps<{
    registration: TimeRegistration,
    context: JobContext
  }>();

  const i18n = useI18n();

  const selectedJobs = ref([]) as Ref<Array<Job>>;

  const VALID_WORK_STATUSES = [WorkStatus.BEING_HANDLED, WorkStatus.ON_HOLD];

  const disabledJobs = computed(() => props.registration.jobs.filter(job => !VALID_WORK_STATUSES.includes(job.workStatus)));

  const selectableJobs = computed(() => props.registration.jobs.filter(job => !disabledJobs.value.includes(job)));

  const icon = computed(() => props.registration.stoppedOn ? 'stop' : 'play');

  /**
   * No jobs selected is considered as all selected
   */
  const hasAllSelected = computed(() => selectedJobs.value.length === 0 || selectedJobs.value.length === selectableJobs.value.length);

  const timeUnitQuantity = computed(() => {
    const { stoppedOn, startedOn } = props.registration;
    const minutesPerUnit = props.context.timeRegistrationContainer?.minutesPerUnit;
    if (startedOn && stoppedOn && isPresent(minutesPerUnit)) {
      return Math.round(moment.duration(stoppedOn.diff(startedOn)).asMinutes() / minutesPerUnit);
    }
  });

  async function finish() {
    const jobs = hasAllSelected.value ? selectableJobs.value : selectedJobs.value;
    const hasAllJobsStopped = await TimeRegistrationService.stop(props.registration.id, jobs, props.context);
    selectedJobs.value = [];
    if (!hasAllJobsStopped) await Modals.message(i18n.t('timeRegistrationModal.notAllJobsStopped'));
  }

  async function pause() {
    await JobService.loadPauseReasons();
    await Modal.open(PauseTimeRegistrationModal, {
      jobs: hasAllSelected.value ? selectableJobs.value : selectedJobs.value,
      timeRegistrationId: props.registration.id,
      context: props.context,
    });
    selectedJobs.value = [];
  }
</script>

<style lang="scss">
  .c-time-registration-card {
    margin-top: 1rem;

    &-title {
      font-weight: 600;
      text-align: center;
    }
  }
</style>